
import React, { useState, useEffect } from 'react';
import { verifyEmailWithCode, resetPassword } from '../services/storageService';
import { CheckCircle2, XCircle, Loader2, ShieldCheck, MailCheck, KeyRound, Eye, EyeOff } from 'lucide-react';

interface EmailActionProps {
  mode: string;
  oobCode: string;
  onComplete: () => void;
}

const EmailAction: React.FC<EmailActionProps> = ({ mode, oobCode, onComplete }) => {
  const [status, setStatus] = useState<'IDLE' | 'VERIFYING' | 'SUCCESS' | 'ERROR'>('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleVerify = async () => {
    if (mode === 'verifyEmail') {
      setStatus('VERIFYING');
      try {
        await verifyEmailWithCode(oobCode);
        setStatus('SUCCESS');
        setTimeout(() => {
          onComplete();
        }, 3000);
      } catch (err: any) {
        console.error("Verification error:", err);
        setStatus('ERROR');
        setErrorMessage(err.message || "Kodi i verifikimit është i pavlefshëm ose ka skaduar.");
      }
    } else if (mode === 'resetPassword') {
      if (!newPassword || newPassword.length < 6) {
        setErrorMessage("Fjalëkalimi duhet të jetë të paktën 6 karaktere.");
        return;
      }
      setStatus('VERIFYING');
      try {
        await resetPassword(oobCode, newPassword);
        setStatus('SUCCESS');
        setTimeout(() => {
          onComplete();
        }, 3000);
      } catch (err: any) {
        console.error("Reset error:", err);
        setStatus('ERROR');
        setErrorMessage(err.message || "Kodi i resetimit është i pavlefshëm ose ka skaduar.");
      }
    }
  };

  if (mode !== 'verifyEmail' && mode !== 'resetPassword') {
    return (
      <div className="glass-card rounded-[2.5rem] p-8 md:p-12 max-w-md mx-auto border border-white/10 text-center shadow-2xl">
        <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
        <h2 className="text-2xl font-bold text-white mb-4">Veprim i panjohur</h2>
        <p className="text-indigo-200/60">Ky link nuk është i vlefshëm për këtë aplikacion.</p>
        <button 
          onClick={onComplete}
          className="mt-8 w-full py-4 rounded-xl bg-white/5 border border-white/10 text-white font-bold hover:bg-white/10 transition-all"
        >
          Kthehu në Fillim
        </button>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-[2.5rem] p-8 md:p-12 max-w-md mx-auto border border-white/10 animate-in fade-in zoom-in duration-500 text-center relative overflow-hidden shadow-2xl">
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl"></div>
      
      {status === 'IDLE' && (
        <div className="relative z-10">
          <div className="w-24 h-24 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-indigo-500/10 shadow-lg">
            {mode === 'verifyEmail' ? <ShieldCheck className="w-12 h-12 text-indigo-400" /> : <KeyRound className="w-12 h-12 text-indigo-400" />}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white font-fairytale mb-4 leading-tight">
            {mode === 'verifyEmail' ? 'Verifikoni Email-in tuaj' : 'Resetoni Fjalëkalimin'}
          </h2>
          <p className="text-indigo-300/80 text-sm mb-8 leading-relaxed">
            {mode === 'verifyEmail' 
              ? 'Për të siguruar llogarinë tuaj dhe për të parandaluar verifikimet automatike, ju lutem klikoni butonin më poshtë.'
              : 'Ju lutem vendosni fjalëkalimin tuaj të ri më poshtë për të rifituar aksesin në llogarinë tuaj.'}
          </p>

          {mode === 'resetPassword' && (
            <div className="mb-6 space-y-1.5 text-left">
              <label className="block text-[10px] font-black text-indigo-200 uppercase tracking-[0.2em] ml-2">Fjalëkalimi i Ri</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"} 
                  required 
                  placeholder="••••••••"
                  className="w-full px-5 py-4 rounded-2xl bg-white/10 border border-white/20 outline-none text-white text-sm focus:border-indigo-500/50 focus:bg-white/20 transition-all shadow-inner"
                  value={newPassword} 
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (errorMessage) setErrorMessage(null);
                  }}
                />
                <button 
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errorMessage && status === 'IDLE' && (
                <p className="text-red-400 text-[10px] mt-2 ml-2 font-bold">{errorMessage}</p>
              )}
            </div>
          )}
          
          <button 
            onClick={handleVerify}
            className="w-full light-pop-btn font-comic font-bold py-4 rounded-xl text-lg uppercase tracking-wide shadow-xl transition-all flex items-center justify-center gap-3"
          >
            {mode === 'verifyEmail' ? (
              <>Konfirmo Verifikimin <MailCheck className="w-6 h-6" /></>
            ) : (
              <>Ndrysho Fjalëkalimin <KeyRound className="w-6 h-6" /></>
            )}
          </button>
        </div>
      )}

      {status === 'VERIFYING' && (
        <div className="py-10">
          <Loader2 className="w-16 h-16 text-indigo-400 animate-spin mx-auto mb-6" />
          <h2 className="text-xl font-bold text-white mb-2">Duke verifikuar...</h2>
          <p className="text-indigo-300/60 text-sm">Ju lutem prisni një moment.</p>
        </div>
      )}

      {status === 'SUCCESS' && (
        <div className="py-6 animate-in zoom-in duration-500">
          <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-green-500/10 shadow-lg">
            <CheckCircle2 className="w-12 h-12 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Sukses!</h2>
          <p className="text-indigo-300/80 text-sm mb-4">
            {mode === 'verifyEmail' ? 'Email-i juaj u verifikua me sukses.' : 'Fjalëkalimi juaj u ndryshua me sukses.'}
          </p>
          <p className="text-xs text-indigo-300/40">Duke ju ridrejtuar...</p>
        </div>
      )}

      {status === 'ERROR' && (
        <div className="py-6 animate-in shake duration-500">
          <div className="w-24 h-24 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-red-500/10 shadow-lg">
            <XCircle className="w-12 h-12 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Dështoi!</h2>
          <p className="text-red-300/80 text-sm mb-8">{errorMessage}</p>
          
          <button 
            onClick={onComplete}
            className="w-full py-4 rounded-xl bg-white/5 border border-white/10 text-white font-bold hover:bg-white/10 transition-all"
          >
            Kthehu në Fillim
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailAction;
